import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, BookOpen, Filter } from 'lucide-react';

interface Formulierungshilfe {
  id: string;
  kategorie: string;
  lebensaktivitaet: string;
  text: string;
  stichwort: string;
}

interface FormulierungshilfenSearchProps {
  formulierungshilfen: Formulierungshilfe[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  selectedActivity: string;
  setSelectedActivity: (activity: string) => void;
  lebensaktivitaeten: string[];
  onLoadFormulierungshilfen: () => void;
  onAddToPflegeplan: (formulierung: Formulierungshilfe) => void;
}

const FormulierungshilfenSearch = ({
  formulierungshilfen,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  selectedActivity,
  setSelectedActivity,
  lebensaktivitaeten,
  onLoadFormulierungshilfen,
  onAddToPflegeplan
}: FormulierungshilfenSearchProps) => {

  useEffect(() => {
    onLoadFormulierungshilfen();
  }, [searchTerm, selectedCategory, selectedActivity]);

  const getCategoryColor = (kategorie: string) => {
    switch (kategorie) {
      case 'ressource':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'problem':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'ziel':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'massnahme':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryLabel = (kategorie: string) => {
    switch (kategorie) {
      case 'ressource':
        return 'Ressource';
      case 'problem':
        return 'Problem';
      case 'ziel':
        return 'Ziel';
      case 'massnahme':
        return 'Maßnahme';
      default:
        return kategorie;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Formulierungshilfen</h2>
        <p className="text-gray-600">Durchsuchen Sie die Formulierungshilfen nach Henke und fügen Sie sie zu Ihrem Pflegeplan hinzu.</p>
      </div>

      {/* Suchfilter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Suchfilter
          </CardTitle>
          <CardDescription>
            Filtern Sie die Formulierungshilfen nach Kategorie, Lebensaktivität oder Suchbegriff.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Suchbegriff</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Text oder Stichwort suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Kategorie</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Kategorie wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle Kategorien</SelectItem>
                  <SelectItem value="ressource">Ressourcen</SelectItem>
                  <SelectItem value="problem">Probleme</SelectItem>
                  <SelectItem value="ziel">Ziele</SelectItem>
                  <SelectItem value="massnahme">Maßnahmen</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="activity">Lebensaktivität</Label>
              <Select value={selectedActivity} onValueChange={setSelectedActivity}>
                <SelectTrigger>
                  <SelectValue placeholder="Lebensaktivität wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle Aktivitäten</SelectItem>
                  {lebensaktivitaeten.map((aktivitaet) => (
                    <SelectItem key={aktivitaet} value={aktivitaet}>
                      {aktivitaet}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 text-sm text-gray-600">
            <span>Gefunden: {formulierungshilfen.length} Formulierungen</span>
            {(searchTerm || selectedCategory !== 'alle' || selectedActivity !== 'alle') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('alle');
                  setSelectedActivity('alle');
                }}
                className="h-auto p-1 text-blue-600 hover:text-blue-800"
              >
                Filter zurücksetzen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Ergebnisliste */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Suchergebnisse</h3>
        </div>

        {formulierungshilfen.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Keine Formulierungshilfen gefunden.</p>
              <p className="text-sm text-gray-500">Versuchen Sie andere Suchbegriffe oder Filter.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {formulierungshilfen.map((formulierung) => (
              <Card key={formulierung.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={getCategoryColor(formulierung.kategorie)}>
                          {getCategoryLabel(formulierung.kategorie)}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {formulierung.lebensaktivitaet}
                        </Badge>
                        {formulierung.stichwort && (
                          <Badge variant="secondary" className="text-xs">
                            {formulierung.stichwort}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-gray-900 leading-relaxed">
                        {formulierung.text}
                      </p>
                    </div>
                    
                    <Button
                      size="sm"
                      onClick={() => onAddToPflegeplan(formulierung)}
                      className="flex items-center gap-1 shrink-0"
                    >
                      <Plus className="h-3 w-3" />
                      Hinzufügen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Legende */}
      <Card className="bg-gray-50">
        <CardHeader>
          <CardTitle className="text-sm">Kategorien-Legende</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800 border-green-200">Ressource</Badge>
              <span className="text-gray-600">Vorhandene Fähigkeiten und Stärken</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-100 text-red-800 border-red-200">Problem</Badge>
              <span className="text-gray-600">Pflegeprobleme und Defizite</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-800 border-blue-200">Ziel</Badge>
              <span className="text-gray-600">Angestrebte Pflegeziele</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-purple-100 text-purple-800 border-purple-200">Maßnahme</Badge>
              <span className="text-gray-600">Konkrete Pflegemaßnahmen</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormulierungshilfenSearch;